import { useState, useEffect } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Typography,
    Alert,
    CircularProgress,
    TextField,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { selectCurrentAccessToken } from '../../../authentication/authSlice';

const ReassignEventOwner = ({ event, onSuccess }) => {
    const [users, setUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const accessToken = useSelector(selectCurrentAccessToken);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/users`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch users');
            }

            const data = await response.json();
            setUsers(data);
        } catch (err) {
            console.error('Error fetching users:', err);
            setError('Käyttäjien hakeminen epäonnistui');
        }
    };

    const handleReassign = async () => {
        if (!selectedUserId) {
            setError('Valitse käyttäjä');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/tapahtumat/reassign-ownership`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    eventId: event.id,
                    newUserId: selectedUserId,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || 'Tapahtuman siirtäminen epäonnistui');
            }

            const data = await response.json();
            setSuccess('Tapahtuma siirretty onnistuneesti!');

            if (onSuccess) {
                setTimeout(() => {
                    onSuccess(data.event);
                }, 1500);
            }
        } catch (err) {
            console.error('Error reassigning event:', err);
            setError(err.message || 'Tapahtuman siirtäminen epäonnistui');
        } finally {
            setLoading(false);
        }
    };

    const currentOwner = users.find(u => u.id === event.user?.id);
    const availableUsers = users
        .filter(u => u.id !== event.user?.id)
        .sort((firstUser, secondUser) => firstUser.name.localeCompare(secondUser.name, 'fi'));
    const selectedUser = availableUsers.find(user => user.id === selectedUserId) || null;

    return (
        <Box sx={{
            p: 3,
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: 2,
            bgcolor: 'grey.50',
            my: 2
        }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Siirrä tapahtuman omistajuus
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}

            <Typography variant="body2" sx={{ mb: 2 }}>
                Nykyinen omistaja: <strong>{currentOwner?.name || event.user?.name || 'Tuntematon'}</strong>
            </Typography>

            <Autocomplete
                fullWidth
                options={availableUsers}
                value={selectedUser}
                onChange={(_, newValue) => setSelectedUserId(newValue?.id || '')}
                disabled={loading}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) => `${option.name} (${option.username})`}
                noOptionsText="Käyttäjiä ei löytynyt"
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Siirrä tapahtuma käyttäjälle"
                        placeholder="Hae nimellä tai käyttäjätunnuksella"
                        sx={{ mb: 2 }}
                    />
                )}
            />

            <Button
                variant="contained"
                onClick={handleReassign}
                disabled={loading || !selectedUserId}
                sx={{
                    bgcolor: 'taphaPurple.main',
                    '&:hover': {
                        bgcolor: 'taphaPurple.dark'
                    }
                }}
            >
                {loading ? <CircularProgress size={24} /> : 'Siirrä tapahtuma'}
            </Button>

            <Typography variant="caption" display="block" sx={{ mt: 2, color: 'text.secondary' }}>
                Huom: Tapahtuma siirtyy valitun käyttäjän omistukseen ja näkyy heidän tapahtumalistassaan.
            </Typography>
        </Box>
    );
};

export default ReassignEventOwner;
